import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Button } from '@/components/ui/button';

export interface DateRangeFilterProps {
  /** ISO datetime-local string (YYYY-MM-DDTHH:MM) or empty. */
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}

export function DateRangeFilter({ from, to, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);

  const label = from || to
    ? `${from ? new Date(from).toLocaleDateString() : '…'} → ${to ? new Date(to).toLocaleDateString() : '…'}`
    : 'Any date';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 shadow-sm hover:bg-slate-50"
      >
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-1 w-80 space-y-3 rounded-md border border-slate-200 bg-white p-3 shadow-lg">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">From</label>
              <DateTimePicker
                value={from}
                onChange={(v) => onChange({ from: v, to })}
                defaultTimeOnDateSelect="00:00"
                placeholder="Pick a start date"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">To</label>
              <DateTimePicker
                value={to}
                onChange={(v) => onChange({ from, to: v })}
                defaultTimeOnDateSelect="23:59"
                placeholder="Pick an end date"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => onChange({ from: '', to: '' })}>
                Clear
              </Button>
              <Button size="sm" onClick={() => setOpen(false)}>Done</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
